/* eslint-disable react-hooks/exhaustive-deps */
import debounce from "debounce-promise"
import React, {useMemo} from "react"
import {useTranslation} from "react-i18next"
import {userService} from "services"
import {KlassDropAsyncPaginate, KlassDropdownAsyncProps} from "../Select"
import {UserAutocomplete} from "types/user"
import {getFullName} from "helpers"
import styles from "./StaffSelect.module.css"
import {Auth} from "types/auth"
import {BaseDepartmentId} from "types/departments"
import {Permissions} from "@edular/permissions"

type Props = Partial<KlassDropdownAsyncProps> & {
    departmentId?: number
}

const StaffSelect: React.FC<Props> = ({departmentId, ...props}) => {
    const {t} = useTranslation(["common"])

    const staffPermissionIds = useMemo(() => {
        switch (departmentId) {
            case BaseDepartmentId.Admissions:
                return [
                    Permissions.Staff.Admissions.AdmissionsStaff.View,
                    Permissions.Staff.Admissions.AdmissionsStaff.PermissionsToAccessOthers.View
                ]
            case BaseDepartmentId.FinancialAid:
                return [
                    Permissions.Staff.FinancialAid.FinancialAidStaff.View,
                    Permissions.Staff.FinancialAid.FinancialAidStaff.PermissionsToAccessOthers.View
                ]
            default:
                return []
        }
    }, [departmentId])

    const search = React.useCallback(
        debounce(async (search: string = "", loadedOptions) => {
            const pageSize = 20
            const page = Math.ceil(loadedOptions.length / pageSize) + 1
            const {data, total} = await userService.searchUsers({
                filters: {
                    type: Auth.UserProfileType.Staff,
                    search,
                    hasPermissionIds: staffPermissionIds
                },
                range: {
                    page,
                    pageSize
                }
            })
            return {
                options: data,
                hasMore: loadedOptions.length < total
            }
        }, 500),
        [staffPermissionIds]
    )

    return (
        <KlassDropAsyncPaginate
            {...props}
            placeholder={props.placeholder || t("message.searchByNameOrEmail")}
            loadOptions={search}
            defaultOptions
            valueKey="userId"
            getOptionLabel={StaffOptionLabel}
        />
    )
}

export default StaffSelect

export const StaffOptionLabel = (option: UserAutocomplete) => {
    return (
        <span>
            <span className={styles.userId}>{option.customProfileId}</span>
            {getFullName(option) || option.name}
        </span>
    )
}
